const CHECK_ORDER = ['applicationConfig', 'securityConfig', 'licenseVerification', 'licenseCheck'];
const listeners = new Set();
const checks = new Map();
let isClosing = false;
let statusFeedbackReady = false;
let latestErrorAt;

export const getStartupValidationSnapshot = () => ({
  checks: [...checks.entries()]
    .sort(([first], [second]) => CHECK_ORDER.indexOf(first) - CHECK_ORDER.indexOf(second))
    .map(([key, value]) => ({ key, ...value })),
  isClosing,
  statusFeedbackReady,
});

const notify = () => {
  const snapshot = getStartupValidationSnapshot();
  listeners.forEach((listener) => listener(snapshot));
};

export const subscribeToStartupValidation = (listener) => {
  listeners.add(listener);
  listener(getStartupValidationSnapshot());
  return () => listeners.delete(listener);
};

export const waitForStartupCheck = (key) => new Promise((resolve) => {
  const checkIfFinished = () => {
    const current = checks.get(key);
    if (!current || current.status !== 'pending') {
      listeners.delete(checkIfFinished);
      resolve();
    }
  };

  listeners.add(checkIfFinished);
  checkIfFinished();
});

export const beginStartupCheck = (key, label) => {
  const current = checks.get(key) || { label, pendingCount: 0, failed: false, succeededCount: 0, skippedCount: 0 };
  current.pendingCount += 1;
  current.status = 'pending';
  checks.set(key, current);
  notify();
};

export const finishStartupCheck = (key, succeeded, isWarning = false) => {
  const current = checks.get(key);
  if (!current) return;

  current.pendingCount = Math.max(0, current.pendingCount - 1);
  if (succeeded) {
    current.succeededCount += 1;
  } else if (isWarning) {
    current.warning = true;
  } else {
    current.failed = true;
  }
  if (current.pendingCount > 0) {
    current.status = 'pending';
  } else if (current.failed) {
    current.status = 'error';
  } else if (current.warning) {
    current.status = 'warning';
  } else {
    current.status = current.succeededCount ? 'success' : 'skipped';
  }
  if (current.status === 'error') latestErrorAt = Date.now();
  checks.set(key, current);
  notify();
};

export const recordStartupError = () => {
  latestErrorAt = Date.now();
};

export const getStartupValidationErrorTime = () => latestErrorAt;

export const closeStartupValidation = () => {
  checks.forEach((check, key) => {
    if (check.pendingCount > 0) {
      checks.set(key, {
        ...check,
        pendingCount: 0,
        failed: true,
        status: 'error',
      });
    }
  });
  isClosing = true;
  notify();
};

export const skipStartupCheck = (key) => {
  const current = checks.get(key);
  if (!current) return;

  current.pendingCount = Math.max(0, current.pendingCount - 1);
  current.skippedCount += 1;
  if (current.pendingCount > 0) {
    current.status = 'pending';
  } else if (current.failed) {
    current.status = 'error';
  } else {
    current.status = current.succeededCount ? 'success' : 'skipped';
  }
  checks.set(key, current);
  notify();
};

export const enableStartupStatusFeedback = () => {
  statusFeedbackReady = true;
  notify();
};

export const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
